import React, { FC, useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { Button, Icon, StsIconName, Typography } from '@packages/ui/shared';
import { createUseStyles } from 'react-jss';
import { DATE_FORMAT } from '@web/config/constants';
import { Platform } from 'react-native';
import colors from '@packages/core/styles/colors';
import InputPickers from '@web/administration/components/worksheet/inputPickers/InputPickers';
import useTranslationData from '@web/administration/components/worksheet/hooks/useTranslationData';
import { debounce } from 'lodash';

type Type = 'time' | 'date' | 'dateTime';

interface InputPickerHelperProps {
    data: string | null;
    datePlaceholder?: string;
    errorMessage?: string;
    iconDisable?: boolean;
    iconName: StsIconName;
    mainContainerClassName?: string;
    onChange?: (data: string) => void;
    readonly?: boolean;
    type: Type;
}

const useStyles = createUseStyles(() => ({
    inputContainer: {
        alignItems: 'center',
        borderColor: colors.grayFive,
        borderRadius: 3,
        borderWidth: 1,
        flexDirection: 'row',
        height: Platform.select({ web: 40, default: 48 }),
        paddingHorizontal: 12,
    },
    disabledInput: {
        backgroundColor: colors.grayTwo,
        color: colors.grayFour,
    },
    border: {
        border: `1px solid ${colors.grayFour}`,
    },
    errorBorder: {
        border: `1px solid ${colors.redOne}`,
    },
}));

export const InputPickerHelper: FC<InputPickerHelperProps> = ({
    data,
    datePlaceholder = DATE_FORMAT.YYYY_MM_DD,
    errorMessage,
    iconDisable,
    iconName,
    mainContainerClassName = '',
    onChange,
    readonly,
    type,
}) => {
    const classes = useStyles();
    const [inputValue, setInputValue] = useState(data || '');
    const [showPicker, setShowPicker] = useState(false);
    const buttonRef = useRef<HTMLDivElement>(null);
    const { draggableElementsDataTranslate } = useTranslationData();

    const placeholder = useMemo(
        () => ({
            time: draggableElementsDataTranslate.placeholder.time,
            date: datePlaceholder,
            dateTime: draggableElementsDataTranslate.placeholder.dateTime,
        }),
        [datePlaceholder, draggableElementsDataTranslate]
    );

    useEffect(() => {
        if (data) {
            setInputValue(data);
        }
    }, [data]);

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setInputValue(event.target.value);
        if (onChange) {
            onChange(event.target.value);
        }
        setShowPicker(false);
        debounce(() => setShowPicker(true), 10)();
    };

    const triggerInput = useCallback(() => {
        setShowPicker((prev) => !prev);
    }, []);

    return (
        <>
            <div
                className={`d-flex justify-content-between w-100 ${errorMessage ? classes.errorBorder : classes.border}`}
            >
                <div
                    className={`${classes.inputContainer} w-100 d-flex justify-content-between ${readonly ? classes.disabledInput : ''}`}
                >
                    <div
                        className="w-100 d-flex align-contents-center overflow-hidden"
                        onClick={() => setShowPicker(true)}
                    >
                        <input
                            value={inputValue}
                            onChange={handleInputChange}
                            readOnly={readonly}
                            placeholder={placeholder[type]}
                            className={`border-0 ml-3 m-3 ${readonly && classes.disabledInput}`}
                        />
                    </div>
                    <div className="d-flex align-contents-center h-100" style={{ position: 'relative' }}>
                        <InputPickers
                            type={type}
                            show={showPicker}
                            value={inputValue}
                            onChange={onChange}
                            setShowTimePicker={setShowPicker}
                            buttonRef={buttonRef}
                            mainContainerClassName={mainContainerClassName}
                        />
                        <div ref={buttonRef}>
                            <Button
                                disabled={iconDisable || readonly}
                                variant="link-header-button"
                                onPress={triggerInput}
                                style={{ marginRight: 10, marginLeft: 50, height: '100%' }}
                            >
                                <Icon name={iconName} color="graySix" style={{ fontWeight: '100' }} />
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
            {errorMessage && (
                <Typography color="redOne" style={{ textAlign: 'center' }}>
                    {errorMessage}
                </Typography>
            )}
        </>
    );
};
